import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2, Star, StarOff, Copy } from 'lucide-react';
import { useIngredients } from '../../../api/queries/inventory.queries';
import type { Formula, RecipeIngredient } from '../../../api/queries/recipe.queries';

interface FormulaEditorProps {
  formula: Formula;
  onChange: (formula: Formula) => void;
  onSetDefault?: () => void;
  onDelete?: () => void;
  onCopy?: () => void;
  isOnlyFormula?: boolean;
}

function FormulaEditor({
  formula,
  onChange,
  onSetDefault,
  onDelete,
  onCopy,
  isOnlyFormula = false,
}: FormulaEditorProps) {
  useTranslation(); // Keep for future i18n
  const { data: ingredients } = useIngredients();
  const [showAddIngredient, setShowAddIngredient] = useState(false);
  const [selectedIngredientId, setSelectedIngredientId] = useState('');
  const [quantity, setQuantity] = useState('');
  const [unit, setUnit] = useState('ml');

  const getIngredientName = (ingredientId: string | { _id: string; name: string; sku: string }) => {
    if (typeof ingredientId === 'object' && ingredientId.name) {
      return ingredientId.name;
    }
    const item = ingredients?.find((i) => i._id === ingredientId);
    return item?.name || String(ingredientId);
  };

  const handleAddIngredient = () => {
    if (!selectedIngredientId || !quantity) return;

    const newIngredient: RecipeIngredient = {
      ingredientId: selectedIngredientId,
      quantity: parseFloat(quantity),
      unit,
    };

    onChange({
      ...formula,
      ingredients: [...formula.ingredients, newIngredient],
    });

    setSelectedIngredientId('');
    setQuantity('');
    setShowAddIngredient(false);
  };

  const handleRemoveIngredient = (index: number) => {
    onChange({
      ...formula,
      ingredients: formula.ingredients.filter((_, i) => i !== index),
    });
  };

  const handleUpdateIngredient = (index: number, field: keyof RecipeIngredient, value: any) => {
    const updated = [...formula.ingredients];
    updated[index] = { ...updated[index], [field]: value };

    onChange({
      ...formula,
      ingredients: updated,
    });
  };

  const handleNameChange = (name: string) => {
    onChange({ ...formula, name });
  };

  return (
    <div className={`rounded-lg border ${formula.isDefault ? 'border-primary ring-1 ring-primary' : 'bg-card'}`}>
      {/* Header */}
      <div className="p-3 border-b flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 flex-1">
          <span className="text-xs font-bold bg-muted px-2 py-1 rounded">
            {formula.formulaCode}
          </span>
          <input
            type="text"
            value={formula.name}
            onChange={(e) => handleNameChange(e.target.value)}
            className="flex-1 text-sm font-medium bg-transparent border-none focus:outline-none focus:ring-0"
            placeholder="Formula name..."
          />
        </div>

        <div className="flex items-center gap-1">
          {formula.isDefault ? (
            <span className="text-xs px-2 py-0.5 rounded bg-primary text-primary-foreground flex items-center gap-1">
              <Star className="h-3 w-3" />
              Default
            </span>
          ) : (
            onSetDefault && (
              <button
                onClick={onSetDefault}
                className="p-1 hover:bg-secondary rounded transition-colors text-muted-foreground hover:text-foreground"
                title="Set as default"
              >
                <StarOff className="h-4 w-4" />
              </button>
            )
          )}

          {onCopy && (
            <button
              onClick={onCopy}
              className="p-1 hover:bg-secondary rounded transition-colors text-muted-foreground hover:text-foreground"
              title="Copy formula"
            >
              <Copy className="h-4 w-4" />
            </button>
          )}

          {onDelete && !isOnlyFormula && (
            <button
              onClick={onDelete}
              className="p-1 hover:bg-destructive/10 rounded transition-colors text-destructive"
              title="Delete formula"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {/* Ingredients list */}
      <div className="p-3 space-y-2">
        {formula.ingredients.length === 0 ? (
          <div className="text-center py-4 text-sm text-muted-foreground">
            No ingredients added
          </div>
        ) : (
          formula.ingredients.map((ingredient, index) => (
            <div
              key={index}
              className="flex items-center gap-2 p-2 rounded bg-secondary/50"
            >
              <div className="flex-1">
                <div className="text-sm font-medium">
                  {getIngredientName(ingredient.ingredientId)}
                </div>
              </div>
              <input
                type="number"
                value={ingredient.quantity}
                onChange={(e) =>
                  handleUpdateIngredient(index, 'quantity', parseFloat(e.target.value))
                }
                className="w-20 rounded border bg-background px-2 py-1 text-sm text-right"
                min="0"
                step="0.01"
              />
              <select
                value={ingredient.unit}
                onChange={(e) => handleUpdateIngredient(index, 'unit', e.target.value)}
                className="w-20 rounded border bg-background px-2 py-1 text-sm"
              >
                <option value="ml">ml</option>
                <option value="g">g</option>
                <option value="pcs">pcs</option>
                <option value="kg">kg</option>
                <option value="l">l</option>
              </select>
              <button
                onClick={() => handleRemoveIngredient(index)}
                className="p-1 rounded hover:bg-destructive/10 text-destructive transition-colors"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))
        )}

        {/* Add ingredient form */}
        {showAddIngredient ? (
          <div className="flex items-center gap-2 p-2 rounded border border-dashed">
            <select
              value={selectedIngredientId}
              onChange={(e) => setSelectedIngredientId(e.target.value)}
              className="flex-1 rounded border bg-background px-2 py-1 text-sm"
            >
              <option value="">Select ingredient...</option>
              {ingredients?.map((item) => (
                <option key={item._id} value={item._id}>
                  {item.name} ({item.sku})
                </option>
              ))}
            </select>
            <input
              type="number"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              placeholder="Qty"
              className="w-20 rounded border bg-background px-2 py-1 text-sm"
              min="0"
              step="0.01"
            />
            <select
              value={unit}
              onChange={(e) => setUnit(e.target.value)}
              className="w-20 rounded border bg-background px-2 py-1 text-sm"
            >
              <option value="ml">ml</option>
              <option value="g">g</option>
              <option value="pcs">pcs</option>
              <option value="kg">kg</option>
              <option value="l">l</option>
            </select>
            <button
              onClick={handleAddIngredient}
              className="px-2 py-1 rounded bg-primary text-primary-foreground text-sm hover:bg-primary/90"
            >
              Add
            </button>
            <button
              onClick={() => setShowAddIngredient(false)}
              className="px-2 py-1 rounded border text-sm hover:bg-secondary"
            >
              Cancel
            </button>
          </div>
        ) : (
          <button
            onClick={() => setShowAddIngredient(true)}
            className="w-full py-2 rounded border border-dashed hover:bg-secondary transition-colors flex items-center justify-center gap-2 text-sm text-muted-foreground"
          >
            <Plus className="h-4 w-4" />
            Add Ingredient
          </button>
        )}
      </div>
    </div>
  );
}

export default FormulaEditor;
